// The one place a page asks for an ad.
//
// Two portals serve ads and no page should know which: CrazyGames (services/crazygames.ts)
// and Y8 (services/y8.ts). Each wrapper is already a hard no-op off its own portal, so
// this module holds no state of its own — it only routes, and every function resolves
// harmlessly on itch.io, Game Jolt, Indie DB and a direct login.
//
// Placement names are passed through because Y8/Google's API takes one and their reporting
// groups by it; CrazyGames has no equivalent field and ignores it.
import { useAdsEnabled as useCrazyAdsEnabled, requestMidgameAd, requestRewardedAd } from './crazygames';
import { useY8AdsEnabled, y8Interstitial, y8OpeningAd, y8Rewarded } from './y8';
import { isCrazyGames, isY8 } from './portal';

/**
 * True only on a portal that serves ads AND with that portal's master switch on. Gates the
 * "+20%" buttons.
 *
 * Both hooks are called unconditionally — a hook behind an `if` breaks the rules of hooks,
 * and each returns false off its own portal, so the OR is exact.
 */
export function useAdsEnabled(): boolean {
  const crazy = useCrazyAdsEnabled();
  const y8 = useY8AdsEnabled();
  return crazy || y8;
}

/**
 * An interstitial at a natural stopping point — a moment the player has already paused at
 * to read a result. Never call this while anything is running on a clock: an interstitial
 * is a full-screen overlay, and a minigame's countdown belongs to the server and is never
 * paused, so a break during an attempt would silently eat the player's time.
 *
 * Rate-limited per portal: CrazyGames by our own 5-minute floor, Y8 by Y8 itself (their
 * docs say to call at every natural break and let them cap it).
 */
export function requestInterstitialAd(name: string): Promise<void> {
  if (isCrazyGames) return requestMidgameAd();
  if (isY8) return y8Interstitial('next', name);
  return Promise.resolve();
}

/**
 * The opening break, at most once per page load.
 *
 * Y8-only: it maps to their 'start' placement ("the game has loaded, the UI is visible,
 * the player can interact, but game play has not started yet"), and our CrazyGames
 * integration has no equivalent — its midgame ad is for mid-session breaks and adding an
 * opening one there would be a change to a live portal that nobody asked for.
 *
 * The CALLER decides who sees it. It must never reach a player who has not played yet.
 */
export function requestOpeningAd(name: string): Promise<void> {
  if (isY8) return y8OpeningAd(name);
  return Promise.resolve();
}

/**
 * A rewarded ad the player opted into. Resolves true ONLY if it was actually watched, so
 * the caller pays the +20% on true and the base amount on everything else — unavailable,
 * dismissed early, capped, timed out, errored. A failure is never shown to the player.
 */
export function requestRewardedAdForBonus(name: string): Promise<boolean> {
  if (isCrazyGames) return requestRewardedAd();
  if (isY8) return y8Rewarded(name);
  return Promise.resolve(false);
}
